import PropTypes from "prop-types";
import React, { useCallback } from "react";
import ReactSelect, { createFilter } from "react-select";
import CreatableSelect from "react-select/creatable";
import { NOOP_FALSE } from "~/constants/callbacks";
import { baseSelectPropTypes } from "~/types/selectProps";
import Control from "./components/Control";
import Option from "./components/Option";
import { styles } from "./components/styles";
import { useSelectColor } from "./hooks/useColor";
import { useEnhancedOptions } from "./hooks/useEnhancedOptions";
import { useTransformedValue } from "./hooks/useTransformedValue";
import { useValidateOptionValue } from "./hooks/useValidateOptionValue";

// TODO: Implement MenuList
// import MenuList from './SelectWrapper/components/MenuList';

const Select = ({
  id = "",
  value = "",
  name = "dropdown",
  placeholder = "Select an Option",
  options = [],
  onChange = NOOP_FALSE,
  isMulti = false,
  isCreatable = false,
  isClearable = false,
  isInvalid = false,
  isSearchable = true,
  isLoading = false,
  isDisabled = false,
  addBlank = false,
  validateValue = false,
  skipValues = ["-1"],
  tooltipText = "",
  tooltipPlacement = "bottom",
  tooltipClassName = "",
  filterOption,
  components: customComponents = {},
  ...restProps
}) => {
  const Component = isCreatable ? CreatableSelect : ReactSelect;

  const color = useSelectColor();
  const enhancedOptions = useEnhancedOptions(options, addBlank);
  const transformedValue = useTransformedValue(value, enhancedOptions, isMulti);
  useValidateOptionValue(value, enhancedOptions, onChange, {
    enabled: validateValue,
    isLoading,
    skipValues,
  });

  const handleChange = useCallback(
    (selected) => {
      onChange(selected);
    },
    [onChange],
  );

  const mergedComponents = {
    Option,
    Control,
    ...customComponents,
  };

  const finalFilterOption =
    filterOption === undefined
      ? createFilter({ ignoreAccents: false })
      : filterOption;

  return (
    <>
      <Component
        openMenuOnFocus
        inputId={id}
        name={name}
        filterOption={finalFilterOption}
        components={mergedComponents}
        placeholder={placeholder}
        options={enhancedOptions}
        onChange={handleChange}
        value={transformedValue}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        styles={styles(color, isInvalid)}
        tooltipText={tooltipText}
        tooltipPlacement={tooltipPlacement}
        tooltipClassName={tooltipClassName}
        {...restProps}
      />
    </>
  );
};

Select.propTypes = {
  ...baseSelectPropTypes,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  filterOption: PropTypes.func,
  isLoading: PropTypes.bool,
  isCreatable: PropTypes.bool,
  validateValue: PropTypes.bool,
  skipValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};

export default Select;
